import type { JSX } from 'react';
import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import { isDefined, thenElse } from 'dg-web-shared/lib/MaybeV2.ts';
import { Conditional } from 'dg-web-shared/lib/ReactHelpers.tsx';
import { ClearanceCreateSlideIn } from '../../clearance-detail/components/ClearanceCreateSlideIn.tsx';
import { ClearanceEditValidityFromDateSlideIn } from '../../clearance-detail/components/ClearanceEditValidityFromDateSlideIn.tsx';
import { ClearanceEditValidityToDateSlideIn } from '../../clearance-detail/components/ClearanceEditValidityToDateSlideIn.tsx';
import {
    ClearanceLpCountrySlideIn,
    ClearancePermitTypeSelectionSlideIn,
    ClearanceValidFromSlideIn,
    ClearanceValidToSlideIn,
    PriceMultiplierSelectionSlideIn,
} from '../../clearance-detail/components/ClearanceSecondLevelSlideIns.tsx';
import { ClearanceSlideIn } from '../../clearance-detail/components/ClearanceSlideIn.tsx';
import { LicensePlateDetailLevel2SlideIns } from '../../common/components/LicensePlateDetailSlideIn.tsx';
import { NoPermission } from '../../common/components/NoPermission.tsx';
import { Translation } from '../../common/i18n/Text.ts';
import * as CurrentOperatorLoginState from '../../common/state/CurrentOperatorLoginState.ts';
import * as MetaState from '../../common/state/MetaState.ts';
import * as OperatorDataState from '../../common/state/OperatorDataState.ts';
import * as PermitTypesState from '../../common/state/PermitTypeState.ts';
import { AddBadgeSlideIn } from '../../permit-detail/components/AddBadgeSlideIn.tsx';
import { AddLicensePlateCountrySlideIn } from '../../permit-detail/components/AddLicensePlateCountrySlidein.tsx';
import { AddLicensePlateSlideIn } from '../../permit-detail/components/AddLicensePlateSlideIn.tsx';
import { AddressSlideIn } from '../../permit-detail/components/Address.tsx';
import {
    AddressCountrySelectionSlideIn,
    setAddressCountry,
} from '../../permit-detail/components/AddressCountrySelectionSlideIn.tsx';
import { CounterPaymentChannelSelectionSlideIn } from '../../permit-detail/components/CounterPaymentChannel.tsx';
import { PermitCreateValidityDurationSlideIn } from '../../permit-detail/components/PermitCreateValidityDurationSlideIn.tsx';
import { PermitCreateValidityFromDateSlideIn } from '../../permit-detail/components/PermitCreateValidityFromDateSlideIn.tsx';
import { PermitCreateValidityToDateSlideIn } from '../../permit-detail/components/PermitCreateValidityToDateSlideIn.tsx';
import { PermitLicensePlateSlidein } from '../../permit-detail/components/PermitLicensePlateSlidein.tsx';
import { PermitSlideIn } from '../../permit-detail/components/PermitSlideIn.tsx';
import { PermitTypeSelectionSlideIn } from '../../permit-detail/components/PermitTypeSelectionSlideIn.tsx';
import { PrintSelectionSlideIn } from '../../permit-detail/components/PrintSelectionSlideIn.tsx';
import { PermitCancellationSlideIn } from '../../permit-detail/components/PermitCancellationSlideIn.tsx';
import { VignetteAmountSlideIn } from '../../permit-detail/components/VignetteAmountSlideIn.tsx';
import { ZoneSelectionSlideIn } from '../../permit-detail/components/ZoneSelectionSlideIn.tsx';
import * as AddressState from '../../permit-detail/state/AddressState.ts';
import * as PermitCreateState from '../../permit-detail/state/PermitCreateState.ts';
import { Body, Container, TabItem, Tabs } from '../../ui/layout/TabContent.tsx';
import { selectFilteredClearancesTab } from '../actions/FilteredClearancesActions.ts';
import { selectFilteredPermitsTab } from '../actions/FilteredPermitsActions.ts';
import { permitListTexts } from '../i18n/ClearancePermitListTexts.tsx';
import * as ClearancePermitListState from '../state/ClearancePermitListState.ts';
import { SelectedTab } from '../state/ClearancePermitListState.ts';
import * as FilteredClearancesState from '../state/FilteredClearancesState.ts';
import * as FilteredPermitsState from '../state/FilteredPermitsState.ts';
import * as SearchState from '../state/SearchState.ts';
import {
    FilteredClearances,
    renderClearanceFilterSlideIns,
} from './filtered-clearances/FilteredClearances.tsx';
import {
    FilteredPermits,
    renderPermitFilterSlideIns,
} from './filtered-permits/FilteredPermits.tsx';
import { ResultList } from './results/ResultsList.tsx';
import { SearchContainer } from './search/Search.tsx';
import * as ResultState from '../state/ResultState.ts';
import { BaseAbstractLegacyServerState } from 'dg-web-shared/lib/AbstractLegacyServerStateSlice.ts';
import { ClearanceRequestsList } from '../../clearance-requests/ClearanceRequestsList.tsx';
import { DetailSlideIn } from '../../clearance-requests/Detail.tsx';
import * as ClearanceRequestsState from '../../clearance-requests/ClearanceRequestsState.ts';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';

export interface Texts {
    CSVDownloadTooltip: Translation;
    FilterTooltip: Translation;
    HideWhitelists: Translation;
    listTabHeader: Translation;
    permitListTabHeader: Translation;
    clearancesListTabHeader: Translation;
    clearancesRequestHeader: Translation;
    pending: Translation;
}

export const CLEARANCE_PERMIT_SLIDEIN_ID =
    'ClearancePermitListSlideinContainer';

export function ClearancePermitList() {
    const { storeState, update } = useStore(store => {
        const search = SearchState.get(store);
        const context =
            search.searchEntity &&
            search.searchValue &&
            search.searchValue.length > 2
                ? { search: search.searchValue, entity: search.searchEntity }
                : null;
        return {
            permitCreateLayout: PermitCreateState.Layout.get(store),
            createConfig: PermitCreateState.Config.get(store),
            permitTypes: PermitTypesState.get(store),
            lpCountries: MetaState.LicensePlateCountries.get(store),
            addressCountries: MetaState.AddressCountries.get(store),
            operatorData: OperatorDataState.get(store),
            currentLogin: CurrentOperatorLoginState.get(store),
            search: search,
            layout: ClearancePermitListState.Layout.get(store),
            filteredPermitsFilter: FilteredPermitsState.Filter.get(store),
            filteredPermitsList: FilteredPermitsState.List.get(store),
            filteredClearancesFilter: FilteredClearancesState.Filter.get(store),
            filteredClearancesList: FilteredClearancesState.List.get(store),
            clearanceRequests: ClearanceRequestsState.List.get(
                store,
                ClearanceRequestsState.Filter.pendingOnly,
            ),
            addressEdit: AddressState.Edit.get(store),
            result: ResultState.get(store, context),
        };
    });
    const { language } = useLanguage();

    function getDependingStateSlices(): BaseAbstractLegacyServerState[] {
        switch (storeState.layout.selectedTab) {
            case SelectedTab.Search:
                return [storeState.result];
            case SelectedTab.FilteredPermits:
                return [storeState.filteredPermitsList];
            case SelectedTab.FilteredClearances:
                return [storeState.filteredClearancesList];
            default:
                return [];
        }
    }

    function renderTabsBody(): JSX.Element | null {
        switch (storeState.layout.selectedTab) {
            case SelectedTab.Search:
                return <ResultList result={storeState.result} />;

            case SelectedTab.FilteredPermits:
                return <FilteredPermits />;

            case SelectedTab.FilteredClearances:
                return <FilteredClearances />;

            case SelectedTab.ClearanceRequests:
                return <ClearanceRequestsList pendingOnly={true} />;

            default:
                return null;
        }
    }

    function renderOutsideSlideIns(): JSX.Element {
        return (
            <div>
                <PermitSlideIn />
                <PermitCreateValidityFromDateSlideIn />
                <PermitCreateValidityToDateSlideIn />
                <PermitCreateValidityDurationSlideIn />
                <ZoneSelectionSlideIn />
                <PermitLicensePlateSlidein />
                <AddLicensePlateSlideIn />
                <AddBadgeSlideIn />
                <AddressSlideIn />
                <AddressCountrySelectionSlideIn
                    onSelect={v => update(store => setAddressCountry(store, v))}
                    open={storeState.addressEdit.selectAddressCountryOpen}
                    onLeftActionClick={() =>
                        update(store =>
                            AddressState.Edit.stateWrite(store, {
                                selectAddressCountryOpen: false,
                            }),
                        )
                    }
                />
                <LicensePlateDetailLevel2SlideIns outsideBody />
                <VignetteAmountSlideIn />
                <PrintSelectionSlideIn />
                <PermitCancellationSlideIn />
                <AddLicensePlateCountrySlideIn />
                <ClearanceSlideIn />
                <ClearanceCreateSlideIn />
                <ClearanceLpCountrySlideIn />
                <ClearanceValidFromSlideIn />
                <ClearanceValidToSlideIn />
                <PriceMultiplierSelectionSlideIn />
                <ClearancePermitTypeSelectionSlideIn />
                <ClearanceEditValidityFromDateSlideIn />
                <ClearanceEditValidityToDateSlideIn />
                <PermitTypeSelectionSlideIn />

                <CounterPaymentChannelSelectionSlideIn />
                <div id={CLEARANCE_PERMIT_SLIDEIN_ID} />
            </div>
        );
    }

    function renderInsideSlideIns(): JSX.Element[] {
        let slideIns: JSX.Element[] = [];

        switch (storeState.layout.selectedTab) {
            case SelectedTab.FilteredPermits:
                slideIns = slideIns.concat(renderPermitFilterSlideIns());
                break;
            case SelectedTab.FilteredClearances:
                slideIns = slideIns.concat(renderClearanceFilterSlideIns());
                break;
            case SelectedTab.ClearanceRequests:
                slideIns = slideIns.concat([
                    <DetailSlideIn key="clearenceRequestDetail" />,
                ]);
                break;
            default:
        }

        return slideIns;
    }

    function bodyDisabled(): boolean {
        const createEnabled = storeState.permitCreateLayout.createEnabled;
        switch (storeState.layout.selectedTab) {
            case SelectedTab.Search:
                return false;
            case SelectedTab.FilteredPermits:
                return (
                    createEnabled ||
                    storeState.filteredPermitsFilter.filterSlideInVisible
                );
            case SelectedTab.FilteredClearances:
                return (
                    createEnabled ||
                    storeState.filteredClearancesFilter.filterSlideInVisible
                );
            default:
                return false;
        }
    }

    const operator = storeState.operatorData.data;
    const login = storeState.currentLogin.data;
    const layout = storeState.layout;
    const texts = permitListTexts[language];

    if (isDefined(operator) && isDefined(login)) {
        const loginIsNotAllowed =
            !login.permissions.permitsRead &&
            !login.permissions.whitelistCreate;
        if (!operator.licensePlatePermitSettings.active || loginIsNotAllowed) {
            return <NoPermission />;
        }
        const filteredPermitsResultSize =
            storeState.filteredPermitsList.data.size;
        const filteredClearancesResultSize =
            storeState.filteredClearancesList.data.size;
        const openClearanceRequestsCount = storeState.clearanceRequests.data
            ? storeState.clearanceRequests.data.length
            : null;
        return (
            <Container>
                <Tabs>
                    <SearchContainer />
                    <TabItem
                        active={
                            layout.selectedTab === SelectedTab.FilteredPermits
                        }
                        description={thenElse(
                            storeState.operatorData.data,
                            d =>
                                d.licensePlatePermitSettings.whitelistActive
                                    ? texts.permitListTabHeader()
                                    : texts.listTabHeader(),
                            '',
                        )}
                        rightText={
                            isDefined(filteredPermitsResultSize) &&
                            filteredPermitsResultSize > 0
                                ? filteredPermitsResultSize.toString()
                                : null
                        }
                        onClick={() => update(selectFilteredPermitsTab)}
                    />
                    <Conditional
                        c={operator.licensePlatePermitSettings.whitelistActive}
                    >
                        <TabItem
                            active={
                                layout.selectedTab ===
                                SelectedTab.FilteredClearances
                            }
                            description={texts.clearancesListTabHeader()}
                            rightText={
                                isDefined(filteredClearancesResultSize) &&
                                filteredClearancesResultSize > 0
                                    ? filteredClearancesResultSize.toString()
                                    : null
                            }
                            onClick={() => update(selectFilteredClearancesTab)}
                        />
                    </Conditional>
                    <Conditional
                        c={
                            operator.licensePlatePermitSettings
                                .hasClearanceRequests
                        }
                    >
                        <TabItem
                            active={
                                layout.selectedTab ===
                                SelectedTab.ClearanceRequests
                            }
                            description={texts.clearancesRequestHeader()}
                            rightText={openClearanceRequestsCount?.toString()}
                            onClick={() =>
                                update(store =>
                                    ClearancePermitListState.Layout.stateWrite(
                                        store,
                                        {
                                            selectedTab:
                                                ClearancePermitListState
                                                    .SelectedTab
                                                    .ClearanceRequests,
                                        },
                                    ),
                                )
                            }
                            specialColor={
                                openClearanceRequestsCount &&
                                openClearanceRequestsCount > 0
                                    ? 'yellow'
                                    : null
                            }
                        />
                    </Conditional>
                </Tabs>
                <Body
                    disabled={bodyDisabled()}
                    slideIns={renderInsideSlideIns()}
                    dependingStateSlices={getDependingStateSlices()}
                    specialBorderColor={
                        openClearanceRequestsCount &&
                        openClearanceRequestsCount > 0 &&
                        storeState.layout.selectedTab ===
                            SelectedTab.ClearanceRequests
                            ? 'yellow'
                            : null
                    }
                >
                    {renderTabsBody()}
                </Body>

                {renderOutsideSlideIns()}
            </Container>
        );
    } else {
        return null;
    }
}
